<template>
  <div class="personal-notes-list">
    <b-card>
      <b-row align-v="center">
        <b-col md="6"><h1 class="mb-0">My Notes</h1></b-col>
        <b-col md="6" class="d-flex justify-content-end flex-wrap">
          <v-select
            v-model="filterSubjectId"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="employeeListOptions"
            :reduce="(val) => val.id"
            label="value"
            placeholder="Filter by person.."
            class="mr-1 mb-1"
            style="min-width: 220px"
          />
          <b-button variant="primary" class="mb-1" @click="openCreate()">
            <feather-icon icon="PlusIcon" class="mr-50" />
            New Note
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div v-if="!isBusy && notes.length === 0" class="misc-inner p-2 p-sm-3">
      <div class="w-100 text-center">
        <h4 class="mb-1">No notes yet</h4>
        <p class="mb-0">Notes you write about colleagues will show up here.</p>
      </div>
    </div>

    <div v-else ref="scrollContainer">
      <b-card v-for="note in notes" :key="note.hashid" class="note-card">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h5 v-if="note.title" class="mb-25">{{ note.title }}</h5>
            <small class="text-muted">{{ formatDate(note.noted_at) }}</small>
            <div class="mt-25">
              <span class="text-muted small mr-50">About:</span>
              <b-badge
                v-for="subject in note.subjects"
                :key="subject.id"
                pill
                variant="light-secondary"
                class="mr-25"
              >
                {{ subject.name }}
              </b-badge>
            </div>
          </div>
          <div class="text-nowrap">
            <feather-icon
              icon="EditIcon"
              class="cursor-pointer mr-1 text-primary"
              @click="openEdit(note)"
            />
            <feather-icon
              icon="Trash2Icon"
              class="cursor-pointer text-danger"
              @click="confirmDelete(note)"
            />
          </div>
        </div>

        <div class="mt-1">
          <b-badge
            v-for="(tag, index) in note.tags"
            :key="index"
            pill
            variant="light-primary"
            class="mr-50 mb-50"
          >
            {{ tag }}
          </b-badge>
        </div>

        <div class="mt-50 note-content" v-html="note.content"></div>
      </b-card>

      <div v-if="isBusy" class="text-center py-2">
        <b-spinner variant="primary" small />
      </div>
    </div>

    <PersonalNoteForm
      :visible.sync="formVisible"
      :note="editingNote"
      :employee-list-options="employeeListOptions"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import { BCard, BRow, BCol, BButton, BBadge, BSpinner } from "bootstrap-vue";
import vSelect from "vue-select";
import axios from "@axios";
import PersonalNoteForm from "./PersonalNoteForm.vue";

export default {
  name: "PersonalNotesList",
  components: { BCard, BRow, BCol, BButton, BBadge, BSpinner, vSelect, PersonalNoteForm },
  data() {
    return {
      notes: [],
      employeeListOptions: [],
      filterSubjectId: null,
      currentPage: 1,
      lastPage: 1,
      isBusy: false,
      formVisible: false,
      editingNote: null,
    };
  },
  computed: {
    hasMore() {
      return this.currentPage < this.lastPage;
    },
  },
  watch: {
    filterSubjectId() {
      this.fetchNotes(true);
    },
  },
  created() {
    this.fetchEmployeeList();
    this.fetchNotes(true);
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      if (this.isBusy || !this.hasMore) return;
      const scrolledToBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;
      if (scrolledToBottom) {
        this.loadMore();
      }
    },
    fetchEmployeeList() {
      this.$store
        .dispatch("app/get", { url: "list/user-list-for-tag-names" })
        .then((res) => {
          this.employeeListOptions = res.data.data;
        })
        .catch(() => {});
    },
    fetchNotes(reset) {
      if (this.isBusy) return;

      if (reset) {
        this.currentPage = 1;
        this.notes = [];
        this.lastPage = 1;
      }
      this.isBusy = true;
      axios
        .get("/personal-notes", {
          params: {
            page: this.currentPage,
            subject_id: this.filterSubjectId,
          },
        })
        .then((res) => {
          if (reset) {
            this.notes = res.data.data;
          } else {
            // vue-infinite-scroll can fire its scroll handler more than
            // once for the same threshold crossing -- dedupe by hashid so
            // a double-fire never appends the same note twice.
            const seen = new Set(this.notes.map((n) => n.hashid));
            this.notes = this.notes.concat(res.data.data.filter((n) => !seen.has(n.hashid)));
          }
          this.lastPage = res.data.meta.last_page;
          this.currentPage = res.data.meta.current_page;
          this.isBusy = false;
        })
        .catch(() => {
          this.isBusy = false;
        });
    },
    loadMore() {
      if (!this.hasMore || this.isBusy) return;
      this.currentPage += 1;
      this.fetchNotes(false);
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    openCreate() {
      this.editingNote = null;
      this.formVisible = true;
    },
    openEdit(note) {
      this.editingNote = note;
      this.formVisible = true;
    },
    handleSaved() {
      this.fetchNotes(true);
    },
    confirmDelete(note) {
      this.$bvModal
        .msgBoxConfirm("Delete this note? This can't be undone.", {
          okTitle: "Delete",
          okVariant: "danger",
          cancelTitle: "Cancel",
        })
        .then((confirmed) => {
          if (confirmed) this.deleteNote(note);
        });
    },
    deleteNote(note) {
      axios.delete(`/personal-notes/${note.hashid}`).then(() => {
        this.notes = this.notes.filter((n) => n.hashid !== note.hashid);
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
.note-card {
  margin-top: 1rem;
}
.note-content {
  word-break: break-word;
}
</style>
